import { useCreateSoftService } from 'hooks/aaa'
import { useCreateUlySoftService } from 'hooks/urgent.ly'
import { EventTypes } from 'types/events'

import { useCreateUniversalService } from '../universalPayload/useCreateUniversalService'
import { FormState } from 'final-form'
import { FormattedCaller } from 'components/fields/Submit'
import { ProviderAllocation } from 'types/dispatchConfig'
import { ProviderCount } from '../universalPayload/useGetProviderCounts'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useDispatchConfig } from 'hooks/universalPayload'
import { ProviderNames, getNextProvider, getSecondaryProvider } from './utils'

type CreateSoft = (state: FormState<any>, caller: FormattedCaller) => Promise<any>
type CreateUniversal = (state: FormState<any>, caller: FormattedCaller, provider: ProviderNames) => Promise<any>

function createSoftDispatch({
  createAAASoftService,
  createULYSoftService,
  createUniversalService,
}: {
  createAAASoftService: CreateSoft
  createULYSoftService: CreateSoft
  createUniversalService: CreateUniversal
}) {
  return async (state: FormState<any>, caller: FormattedCaller, providerName: String, stateConfig: ProviderAllocation[]) => {
    const { scheduledAt } = state.values

    const secondaryProviderName = getSecondaryProvider(stateConfig)

    if (providerName === ProviderNames.AAA) {
      if (!scheduledAt) {
        return await createAAASoftService(state, caller)
      } else {
        if (secondaryProviderName === ProviderNames.ALLSTATE) {
          return await createUniversalService(state, caller, ProviderNames.ALLSTATE)
        } else {
          return await createULYSoftService(state, caller)
        }
      }
    }
    if (providerName === ProviderNames.URGENTLY) {
      return await createULYSoftService(state, caller)
    }
    return await createUniversalService(state, caller, ProviderNames.ALLSTATE)
  }
}

export const useCreateSoftDispatchService = (eventType: EventTypes, providerCount: ProviderCount) => {
  const { roadsideForceDispatchProvider } = useFlags()
  const getDispatchConfig = useDispatchConfig()
  const createAAASoftService = useCreateSoftService(eventType)
  const createULYSoftService = useCreateUlySoftService(eventType)
  const createUniversalService = useCreateUniversalService(eventType)

  const softDispatch = createSoftDispatch({
    createAAASoftService,
    createULYSoftService,
    createUniversalService,
  })

  return async (state: FormState<any>, caller: FormattedCaller) => {
    // Check LaunchDarkly flag value
    switch (roadsideForceDispatchProvider) {
      case 'uly': // Force ULY provider
        return await createULYSoftService(state, caller)
      case 'aaa': // Force AAA provider
        return await createAAASoftService(state, caller)
      case 'allstate': // Force Allstate provider
        return await createUniversalService(state, caller, ProviderNames.ALLSTATE)
      case 'default':
      default:
        // Fallback to original logic when flag is "default"
        break
    }

    // Ratio/config-based routing
    const { serviceLocations } = state.values
    const config = await getDispatchConfig()
    const vehicleState =
      serviceLocations?.vehicleLocation?.address?.state || serviceLocations?.callerLocation?.address?.state || 'N/A'
    const thisStateConfig =
      (config[vehicleState as keyof typeof config] as ProviderAllocation[]) ||
      (config['default' as keyof typeof config] as ProviderAllocation[])
    const providerName = getNextProvider(thisStateConfig, providerCount)

    return await softDispatch(state, caller, providerName, thisStateConfig)
  }
}
